import { animaliaUseCasesFactory } from './contexts/animalia/application/animaliaUseCasesFactory';
import { ANIMALIA_LIST_FILTER_KEYS } from './contexts/animalia/domain/ports/AnimaliaListRepositoryPort';
import { MvAnimaliaDetailPage } from './contexts/animalia/ui/pages/mv-animalia-detail-page';
import { MvAnimaliaHomePage } from './contexts/animalia/ui/pages/mv-animalia-home-page';
import { FetchHttpClientAdapter } from './core/infrastructure/adapters/FetchHttpClientAdapter';
import { setupMsw } from './mocks/setupMsw';
import { applySeoMetadata, buildSiteUrl, createWebsiteJsonLd } from './shared/ui/seo/seoMetadata';
import { MvAppShell } from './shared/ui/components/mv-app-shell';
import { MvNotFoundPage } from './shared/ui/pages/mv-not-found-page';
import { Router } from './shared/ui/router';

import './shared/ui/components/mv-app-shell';
import './contexts/animalia/ui/pages/mv-animalia-detail-page';
import './shared/ui/pages/mv-not-found-page';
import './contexts/animalia/ui/pages/mv-animalia-home-page';

const HOME_ROUTE = '/';

function applyDefaultRouteSeo(path: string, title: string, description: string, robots = 'index,follow'): void {
  applySeoMetadata({
    title,
    description,
    path,
    canonicalUrl: buildSiteUrl(path),
    robots,
    jsonLd: createWebsiteJsonLd(),
  });
}

async function bootstrap(): Promise<void> {
  await setupMsw();

  const appRoot = document.querySelector<HTMLDivElement>('#app');

  if (!appRoot) {
    throw new Error('Unable to mount the application shell.');
  }

  const appShell = document.createElement('mv-app-shell') as MvAppShell;
  const router = new Router();
  const httpClient = new FetchHttpClientAdapter();
  const animaliaUseCases = animaliaUseCasesFactory(httpClient);

  appRoot.replaceChildren(appShell);

  router.register(HOME_ROUTE, () => {
    const page = document.createElement('mv-animalia-home-page') as MvAnimaliaHomePage;

    page.setUseCases(animaliaUseCases);

    appShell.setCurrentPath(HOME_ROUTE);
    appShell.setView(page, 'Listado editorial');
    applyDefaultRouteSeo(
      HOME_ROUTE,
      'Insectos y fichas editoriales',
      'Archivo editorial de insectos con taxonomias navegables, fichas de especies y contexto naturalista en Bichitos.life.',
    );
  });

  ANIMALIA_LIST_FILTER_KEYS.forEach((filterKey) => {
    router.register(`/${filterKey}/:value`, () => {
      const value = router.getParam('value') ?? '';
      const page = document.createElement('mv-animalia-home-page') as MvAnimaliaHomePage;

      page.setUseCases(animaliaUseCases);

      appShell.setCurrentPath(`/${filterKey}/${encodeURIComponent(value)}`);
      appShell.setView(page, 'Landing taxonomica');
      applyDefaultRouteSeo(
        value ? `/${filterKey}/${encodeURIComponent(value)}` : HOME_ROUTE,
        value ? `${value} ${filterKey}` : 'Landing taxonomica',
        value
          ? `Explora la taxonomia ${filterKey} ${value} con especies relacionadas, contexto editorial y fichas enlazables en Bichitos.life.`
          : 'Explora taxonomias navegables y especies relacionadas dentro del archivo editorial de Bichitos.life.',
      );
    });
  });

  router.register('/animalia/:slug', () => {
    const slug = router.getParam('slug') ?? '';
    const page = document.createElement('mv-animalia-detail-page') as MvAnimaliaDetailPage;

    page.setUseCases(animaliaUseCases);
    page.setSlug(slug);

    appShell.setCurrentPath(`/animalia/${slug}`);
    appShell.setView(page, 'Ficha editorial');
    applyDefaultRouteSeo(
      slug ? `/animalia/${encodeURIComponent(slug)}` : '/animalia',
      slug ? `Ficha de ${slug}` : 'Ficha editorial',
      slug
        ? `Consulta la ficha editorial de ${slug}, con taxonomia navegable, narrativa y referencias naturalistas en Bichitos.life.`
        : 'Consulta fichas editoriales de especies con taxonomia, narrativa y referencias naturalistas en Bichitos.life.',
    );
  });

  router.setFallback(() => {
    const page = document.createElement('mv-not-found-page') as MvNotFoundPage;

    appShell.setCurrentPath('');
    appShell.setView(page, 'Ruta no encontrada');
    applyDefaultRouteSeo(
      window.location.pathname || '/',
      'Ruta no disponible',
      'La pagina solicitada no esta disponible. Explora el archivo editorial de insectos desde la portada de Bichitos.life.',
      'noindex,follow',
    );
  });

  router.start();
}

void bootstrap();
